// Búsqueda de direcciones sobre datos OSM sin depender de Google.
//
// Uso: `photon.komoot.io` (mantenido por Komoot). Es la opción por defecto porque:
//   - no requiere clave,
//   - devuelve resultados en una sola llamada listos para pintar en un autocomplete,
//   - permite locationBias con `lat`/`lon` para sesgar por proximidad,
//   - es rápida (Elasticsearch).
// Requiere respetar la Usage Policy: llamadas moderadas (~1 req/s). Si algún día pasamos los
// límites, el mismo API es compatible con un Photon self-hosted en Dokploy.
//
// Como sub-fallback usamos Nominatim para la resolución final si Photon falla — misma familia,
// idéntica atribución OSM.

import { ELCHE_BOUNDS, ELCHE_CENTER } from "../config";
import type { LatLng } from "../geo";
import type { AddressResult } from "./googlePlaces";

// Sesgo geográfico común: centro de Elche + bounding box.
const BIAS = {
  lat: ELCHE_CENTER.lat,
  lon: ELCHE_CENTER.lon,
  bbox: [ELCHE_BOUNDS.west, ELCHE_BOUNDS.south, ELCHE_BOUNDS.east, ELCHE_BOUNDS.north] as const,
};

interface PhotonFeature {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: {
    osm_id?: number;
    osm_type?: string;
    country?: string;
    countrycode?: string;
    postcode?: string;
    city?: string;
    district?: string;
    street?: string;
    housenumber?: string;
    name?: string;
    type?: string;
  };
}
interface PhotonResponse {
  features: PhotonFeature[];
}

const buildLabel = (f: PhotonFeature): string => {
  const p = f.properties;
  const bits: string[] = [];
  if (p.name) bits.push(p.name);
  if (p.street) bits.push(p.housenumber ? `${p.street} ${p.housenumber}` : p.street);
  if (p.district) bits.push(p.district);
  if (p.city && !bits.includes(p.city)) bits.push(p.city);
  if (bits.length === 0) return f.geometry.coordinates.join(", ");
  return bits.join(", ");
};

const featureToResult = (f: PhotonFeature): AddressResult => ({
  ll: { lat: f.geometry.coordinates[1], lon: f.geometry.coordinates[0] },
  label: buildLabel(f),
});

// Resuelve una consulta libre a la mejor coincidencia. Devuelve null si Photon no encuentra
// nada o si el fetch falla. Sesgado a Elche.
export async function resolveAddress(query: string): Promise<AddressResult | null> {
  const clean = query.trim();
  if (!clean) return null;
  const url = new URL("https://photon.komoot.io/api");
  url.searchParams.set("q", clean);
  url.searchParams.set("limit", "1");
  url.searchParams.set("lang", "es");
  url.searchParams.set("lat", String(BIAS.lat));
  url.searchParams.set("lon", String(BIAS.lon));
  url.searchParams.set("bbox", BIAS.bbox.join(","));
  url.searchParams.set("osm_tag", "!boundary"); // fuera de límites administrativos
  try {
    const r = await fetch(url, { headers: { Accept: "application/json" } });
    if (!r.ok) return null;
    const j = (await r.json()) as PhotonResponse;
    const f = j.features[0];
    return f ? featureToResult(f) : null;
  } catch {
    return null;
  }
}

// Devuelve varias sugerencias para pintar en un autocomplete.
export interface Suggestion {
  label: string;
  ll: LatLng;
}
export async function fetchSuggestions(input: string, limit = 6): Promise<Suggestion[]> {
  const clean = input.trim();
  if (clean.length < 3) return [];
  const url = new URL("https://photon.komoot.io/api");
  url.searchParams.set("q", clean);
  url.searchParams.set("limit", String(limit));
  url.searchParams.set("lang", "es");
  url.searchParams.set("lat", String(BIAS.lat));
  url.searchParams.set("lon", String(BIAS.lon));
  url.searchParams.set("bbox", BIAS.bbox.join(","));
  try {
    const r = await fetch(url, { headers: { Accept: "application/json" } });
    if (!r.ok) return [];
    const j = (await r.json()) as PhotonResponse;
    return j.features.map((f) => ({ label: buildLabel(f), ll: featureToResult(f).ll }));
  } catch {
    return [];
  }
}
